const React = require('react');
const { createElement: h, useState, useEffect } = React;
const templateStore = require('../lib/templateStore');
const { CATEGORY_OPTIONS, getSubcategories } = require('../data/templateCategories');
require('./PublishTemplateModal.css');

// Fields that must be filled before a template can be published. Cover image is
// the only optional one — the store falls back to a category gradient.
var REQUIRED = [
  ['title', 'Title'],
  ['summary', 'Description'],
  ['category', 'Category'],
  ['subcategory', 'Subcategory'],
  ['readme', 'Setup instructions']
];

function field(label, required, input) {
  return h('label', { className: 'ptm-field' },
    h('span', { className: 'ptm-label' }, label, required ? h('span', { className: 'ptm-req' }, ' *') : null),
    input
  );
}

function PublishTemplateModal(props) {
  var workflow = props.workflow || {};
  var [form, setForm] = useState({
    title: workflow.name || '',
    summary: '',
    category: '',
    subcategory: '',
    connectors: '',
    coverImage: '',
    videoUrl: '',
    readme: '',
    changelog: ''
  });
  var [saving, setSaving] = useState(false);
  var [error, setError] = useState('');
  var [done, setDone] = useState(false);
  var [aiBusy, setAiBusy] = useState(false);
  // Prior store listing for this workflow (null = first publish). Drives the
  // re-publish banner + the changelog field.
  var [existing, setExisting] = useState(null);

  useEffect(function() {
    if (!workflow.id) return undefined;
    var alive = true;
    templateStore.getTemplateForWorkflow(workflow.id)
      .then(function(t) {
        if (!alive || !t) return;
        setExisting(t);
        // Re-publish: pre-fill the form from the current store listing so the
        // user edits the published metadata instead of re-typing it. Only
        // overwrite fields the listing actually has; keep the changelog blank.
        setForm(function(prev) {
          return {
            title: t.title || prev.title,
            summary: t.summary || prev.summary,
            category: t.category || prev.category,
            subcategory: t.subcategory || prev.subcategory,
            connectors: t.connectors || prev.connectors,
            coverImage: t.coverImage || prev.coverImage,
            videoUrl: t.videoUrl || prev.videoUrl,
            readme: t.readme || prev.readme,
            changelog: prev.changelog
          };
        });
      })
      .catch(function() { /* first publish — no banner */ });
    return function() { alive = false; };
  }, [workflow.id]);

  var nextVersion = existing ? (existing.nextVersion || (existing.version || 0) + 1) : 1;
  // The user-facing version follows the LIVE site version (semver), e.g.
  // "1.0.10" — not the store's internal review counter. Falls back to the store
  // counter only if the live version is somehow unavailable.
  var liveVer = workflow.liveVersion || (existing && existing.liveVersion) || '';
  var versionLabel = liveVer ? ('v' + liveVer) : ('v' + nextVersion);

  function update(key, val) {
    var next = Object.assign({}, form);
    next[key] = val;
    setForm(next);
  }

  // Inspect the workflow with Haiku and fill the listing fields. Only overwrites
  // a field when the AI returns a value for it, so manual edits aren't lost.
  function aiFill() {
    setAiBusy(true);
    setError('');
    templateStore.aiFillTemplate(workflow.id)
      .then(function(s) {
        s = s || {};
        setForm(function(prev) {
          return Object.assign({}, prev, {
            title: s.title || prev.title,
            summary: s.summary || prev.summary,
            category: s.category || prev.category,
            subcategory: s.subcategory || prev.subcategory,
            connectors: (s.connectors && s.connectors.length) ? s.connectors.join(', ') : prev.connectors,
            readme: s.readme || prev.readme
          });
        });
        setAiBusy(false);
      })
      .catch(function(e) { setAiBusy(false); setError(e.message || 'AI fill failed.'); });
  }

  // Changing the category resets the subcategory, since the allowed options
  // depend on it.
  function changeCategory(val) {
    setForm(Object.assign({}, form, { category: val, subcategory: '' }));
  }

  function publish() {
    var missing = REQUIRED.filter(function(pair) { return !String(form[pair[0]] || '').trim(); })
      .map(function(pair) { return pair[1]; });
    if (missing.length) { setError('Please fill in: ' + missing.join(', ')); return; }

    setSaving(true);
    setError('');
    templateStore.publishTemplate({
      title: form.title,
      summary: form.summary,
      category: form.category,
      subcategory: form.subcategory,
      connectors: form.connectors,
      coverImage: form.coverImage,
      videoUrl: form.videoUrl,
      readme: form.readme,
      changelog: form.changelog,
      screenshot: workflow.screenshot || '',
      published: true,
      sourceWorkflowId: workflow.id
    })
      .then(function() { setSaving(false); setDone(true); })
      .catch(function(e) { setSaving(false); setError(e.message || 'Publish failed.'); });
  }

  function stop(e) { e.stopPropagation(); }

  return h('div', { className: 'ptm-overlay', onClick: props.onClose },
    h('div', { className: 'ptm-card', onClick: stop },
      h('div', { className: 'ptm-head' },
        h('div', null,
          h('div', { className: 'ptm-title' }, 'Publish to Template Store'),
          h('div', { className: 'ptm-sub' },
            'From workflow: ' + (workflow.name || 'Untitled') + (liveVer ? ' · live v' + liveVer : '')
          )
        ),
        h('button', { type: 'button', className: 'ptm-x', onClick: props.onClose, 'aria-label': 'Close' }, '×')
      ),
      done
        ? h('div', { className: 'ptm-done' },
            h('div', { className: 'ptm-done-icon' }, '✓'),
            h('div', { className: 'ptm-done-title' }, versionLabel + ' submitted for review'),
            h('div', { className: 'ptm-done-sub' },
              '"' + form.title + '" (' + versionLabel + ') was sent for review. A super admin will approve it before it goes live' +
              (existing && existing.liveVersion ? '. Your current v' + existing.liveVersion + ' keeps serving installs in the meantime.' : '.')),
            h('button', { type: 'button', className: 'ptm-btn ptm-btn--primary', onClick: function() { props.onPublished && props.onPublished(); } }, 'Done')
          )
        : h('div', { className: 'ptm-body' },
            existing
              ? h('div', { className: 'ptm-banner' },
                  h('span', { className: 'ptm-banner-pill' }, versionLabel),
                  h('span', null,
                    'You’ve published this workflow before' + (existing.liveVersion ? ' (live: v' + existing.liveVersion + ')' : '') +
                    '. Publishing submits ' + versionLabel + ' for review — your live version keeps serving installs until it’s approved.'
                  )
                )
              : null,
            field('Title', true, h('input', { className: 'ptm-input', value: form.title, onChange: function(e) { update('title', e.target.value); }, autoFocus: true })),
            field('Description', true, h('input', { className: 'ptm-input', placeholder: 'One line shown on the store card', value: form.summary, onChange: function(e) { update('summary', e.target.value); } })),
            h('div', { className: 'ptm-row' },
              field('Category', true,
                h('select', { className: 'ptm-input', value: form.category, onChange: function(e) { changeCategory(e.target.value); } },
                  [h('option', { key: '', value: '' }, 'Select category')].concat(
                    CATEGORY_OPTIONS.map(function(c) { return h('option', { key: c, value: c }, c); })
                  )
                )
              ),
              field('Subcategory', true,
                h('select', { className: 'ptm-input', value: form.subcategory, disabled: !form.category, onChange: function(e) { update('subcategory', e.target.value); } },
                  [h('option', { key: '', value: '' }, form.category ? 'Select subcategory' : 'Pick a category first')].concat(
                    getSubcategories(form.category).map(function(s) { return h('option', { key: s, value: s }, s); })
                  )
                )
              )
            ),
            field('Connectors', false, h('input', { className: 'ptm-input', placeholder: 'Comma separated — e.g. Gmail, Jira, Tavily', value: form.connectors, onChange: function(e) { update('connectors', e.target.value); } })),
            h('div', { className: 'ptm-row' },
              field('Cover image URL (optional)', false, h('input', { className: 'ptm-input', placeholder: 'https://… (leave blank for a gradient)', value: form.coverImage, onChange: function(e) { update('coverImage', e.target.value); } })),
              field('YouTube demo video (optional)', false, h('input', { className: 'ptm-input', placeholder: 'https://youtu.be/…', value: form.videoUrl, onChange: function(e) { update('videoUrl', e.target.value); } }))
            ),
            field('Setup instructions (markdown)', true, h('textarea', { className: 'ptm-input ptm-textarea', rows: 8, placeholder: 'How to run this workflow correctly: variables to set, sheets to create, accounts to connect…', value: form.readme, onChange: function(e) { update('readme', e.target.value); } })),
            field(existing ? 'What’s new in ' + versionLabel + ' (optional)' : 'Version notes (optional)', false,
              h('input', { className: 'ptm-input', placeholder: existing ? 'e.g. Fixed the email parsing step, added a retry' : 'A short note about this first version', value: form.changelog, onChange: function(e) { update('changelog', e.target.value); } })),
            error ? h('div', { className: 'ptm-error' }, error) : null,
            h('div', { className: 'ptm-actions' },
              h('button', { type: 'button', className: 'ptm-btn ptm-btn--ai', disabled: aiBusy || saving, onClick: aiFill, title: 'Inspect the workflow and fill these fields with AI' },
                aiBusy ? 'Inspecting workflow…' : '✨ Let AI fill'),
              h('div', { className: 'ptm-actions-right' },
                h('button', { type: 'button', className: 'ptm-btn', onClick: props.onClose }, 'Cancel'),
                h('button', { type: 'button', className: 'ptm-btn ptm-btn--primary', disabled: saving || aiBusy, onClick: publish }, saving ? 'Publishing…' : 'Publish')
              )
            )
          )
    )
  );
}

module.exports = PublishTemplateModal;
